import { z } from "zod";
import { AMAZON_SP_API_AUTH_APP_ID } from "../../../utils/gvSecrets";

const PRODUCTION_APP_URL = "https://app.gotrellis.com";

function getAppUrl(): string {
  return PRODUCTION_APP_URL;
}

export type SpApiRegion = "NA" | "EU" | "FE";

interface SpApiOAuthConfig {
  region: SpApiRegion;
  marketplace: string;
  isSeller: boolean;
  successPath?: string;
  errorPath?: string;
}

const encodedStateSchema = z.object({
  region: z.enum(["NA", "EU", "FE"]),
  successPath: z.string(),
  errorPath: z.string(),
});

type SpApiEncodedState = z.infer<typeof encodedStateSchema>;

// Marketplace to Seller/Vendor Central URL mappings
// NA Region
const SELLER_CENTRAL_URLS: Record<string, string> = {
  US: "https://sellercentral.amazon.com",
  USA: "https://sellercentral.amazon.com",
  CA: "https://sellercentral.amazon.ca",
  MX: "https://sellercentral.amazon.com.mx",
  BR: "https://sellercentral.amazon.com.br",
  // EU Region - uses unified Europe URL for sellers
  UK: "https://sellercentral-europe.amazon.com",
  FR: "https://sellercentral-europe.amazon.com",
  DE: "https://sellercentral-europe.amazon.com",
  GR: "https://sellercentral-europe.amazon.com",
  IT: "https://sellercentral-europe.amazon.com",
  ES: "https://sellercentral-europe.amazon.com",
  BE: "https://sellercentral-europe.amazon.com",
  NL: "https://sellercentral.amazon.nl",
  SE: "https://sellercentral.amazon.se",
  PL: "https://sellercentral.amazon.pl",
  IN: "https://sellercentral.amazon.in",
  AE: "https://sellercentral.amazon.ae",
  SA: "https://sellercentral.amazon.sa",
  IE: "https://sellercentral.amazon.ie",
  TR: "https://sellercentral.amazon.com.tr",
  // FE Region
  SG: "https://sellercentral.amazon.sg",
  AU: "https://sellercentral.amazon.com.au",
  JP: "https://sellercentral.amazon.co.jp",
};

const VENDOR_CENTRAL_URLS: Record<string, string> = {
  US: "https://vendorcentral.amazon.com",
  USA: "https://vendorcentral.amazon.com",
  CA: "https://vendorcentral.amazon.ca",
  MX: "https://vendorcentral.amazon.com.mx",
  BR: "https://vendorcentral.amazon.com.br",
  // EU Region
  UK: "https://vendorcentral.amazon.co.uk",
  FR: "https://vendorcentral.amazon.fr",
  DE: "https://vendorcentral.amazon.de",
  GR: "https://vendorcentral.amazon.de",
  IT: "https://vendorcentral.amazon.it",
  ES: "https://vendorcentral.amazon.es",
  BE: "https://vendorcentral.amazon.com.be",
  NL: "https://vendorcentral.amazon.nl",
  SE: "https://vendorcentral.amazon.se",
  PL: "https://vendorcentral.amazon.pl",
  IN: "https://www.vendorcentral.in",
  AE: "https://vendorcentral.amazon.me",
  SA: "https://vendorcentral.amazon.me",
  IE: "https://vendorcentral.amazon.ie",
  TR: "https://vendorcentral.amazon.com.tr",
  // FE Region
  SG: "https://vendorcentral.amazon.com.sg",
  AU: "https://vendorcentral.amazon.com.au",
  JP: "https://vendorcentral.amazon.co.jp",
};

function getCentralUrl(marketplace: string, isSeller: boolean): string {
  const urlMap = isSeller ? SELLER_CENTRAL_URLS : VENDOR_CENTRAL_URLS;
  const url = urlMap[marketplace];

  if (!url) {
    // Fallback to US URL if marketplace not found
    return isSeller ? SELLER_CENTRAL_URLS.US! : VENDOR_CENTRAL_URLS.US!;
  }

  return url;
}

export function initSpApiOAuth(config: SpApiOAuthConfig): void {
  const defaultSuccessPath = "/onboarding/v2/connect";
  const defaultErrorPath = window.location.pathname + window.location.search;

  const successPath = config.successPath ?? defaultSuccessPath;
  const errorPath = config.errorPath ?? defaultErrorPath;

  const statePayload: SpApiEncodedState = {
    region: config.region,
    successPath,
    errorPath,
  };

  const encodedState = btoa(JSON.stringify(statePayload));
  const redirectUri = `${getAppUrl()}/oauth/amazon/spapi`;

  const centralUrl = getCentralUrl(config.marketplace, config.isSeller);
  const oauthUrl = new URL(`${centralUrl}/apps/authorize/consent`);
  oauthUrl.searchParams.set("application_id", AMAZON_SP_API_AUTH_APP_ID);
  oauthUrl.searchParams.set("state", encodedState);
  oauthUrl.searchParams.set("redirect_uri", redirectUri);

  window.location.href = oauthUrl.toString();
}

export function initSpApiReauth(region: SpApiRegion, marketplace: string, isSeller: boolean): void {
  initSpApiOAuth({
    region,
    marketplace,
    isSeller,
    successPath: "/user/amzsummary?notification=spApiReauthSuccess",
  });
}

export function decodeSpApiOAuthState(encodedState: string | null): SpApiEncodedState | null {
  if (!encodedState) return null;

  try {
    const decoded = JSON.parse(atob(encodedState));
    const result = encodedStateSchema.safeParse(decoded);
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}
